
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AdminHeader } from '@/components/admin/AdminHeader';
import GlobalLoader from '@/components/GlobalLoader';
import { trackPageView } from '@/utils/metaPixel';
import { logPageView } from '@/utils/pageViewTracking';
// Removed performance monitoring hooks for better performance

// Static imports for critical path
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Index from '@/pages/Index';
import About from '@/pages/About';
import NotFound from '@/pages/NotFound';

// Skeleton components removed

// Lazy imports for better code splitting
const LazyShows = React.lazy(() => import('@/pages/Shows'));
const LazyCourses = React.lazy(() => import('@/pages/Courses'));
const LazyCourseLeaderOverview = React.lazy(() => import('@/pages/CourseLeaderOverview'));
const LazyCourseLeaderDetail = React.lazy(() => import('@/pages/CourseLeaderDetail'));
const LazyLokal = React.lazy(() => import('@/pages/Lokal'));
const LazyStudentmanual = React.lazy(() => import('@/pages/Studentmanual'));
const LazyHouseTeam = React.lazy(() => import('@/pages/HouseTeam'));
const LazyBokaOss = React.lazy(() => import('@/pages/BokaOss'));
const LazyShowDetails = React.lazy(() => import('@/pages/ShowDetails'));
const LazyAdmin = React.lazy(() => import('@/pages/Admin'));
const LazyTicketSuccess = React.lazy(() => import('@/pages/TicketPaymentSuccess'));
const LazyCourseSuccess = React.lazy(() => import('@/pages/CoursePaymentSuccess'));
const LazyCourseOfferPayment = React.lazy(() => import('@/pages/CourseOfferPayment'));
const LazyTicketScanning = React.lazy(() => import('@/pages/TicketScanning'));
const LazyTicketView = React.lazy(() => import('@/pages/TicketView'));
const LazyToaster = React.lazy(() => import('@/components/ui/sonner').then(module => ({ default: module.Toaster })));
const LazyPaymentPages = React.lazy(() => 
  Promise.all([
    import('@/pages/TicketPaymentSuccess'),
    import('@/pages/CoursePaymentSuccess'),
    import('@/pages/PaymentCancelled'),
  ]).then(([ticketSuccess, courseSuccess, cancelled]) => ({
    default: () => (
      <Routes>
        <Route path="/ticket-success" element={<ticketSuccess.default />} />
        <Route path="/course-success" element={<courseSuccess.default />} />
        <Route path="/payment-cancelled" element={<cancelled.default />} />
      </Routes>
    )
  }))
);
const LazyNewsletterConfirmation = React.lazy(() => import('@/pages/NewsletterConfirmation'));
const LazyUnsubscribe = React.lazy(() => import('@/pages/Unsubscribe'));
const LazyIntegritet = React.lazy(() => import('@/pages/Integritet'));
const LazyOmImprov = React.lazy(() => import('@/pages/OmImprov'));
const LazyVanligaFragor = React.lazy(() => import('@/pages/VanligaFragor'));
const LazyVarFilosofi = React.lazy(() => import('@/pages/VarFilosofi'));
const LazyPresentkort = React.lazy(() => import('@/pages/Presentkort'));
const LazyPresentkortTack = React.lazy(() => import('@/pages/PresentkortTack'));
const LazySattLosenord = React.lazy(() => import('@/pages/SattLosenord'));
const LazyGaffaRedirect = React.lazy(() => import('@/pages/GaffaRedirect'));

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isScanRoute = location.pathname === '/scan';

  useEffect(() => {
    trackPageView();
    logPageView(location.pathname);
  }, [location.pathname]);


  return (
    <div className="min-h-screen flex flex-col bg-background">
      {!isAdminRoute && !isScanRoute && <Header />}
      <main className="flex-1">
        <Routes>
            {/* Critical routes - loaded immediately */}
            <Route path="/" element={<Index />} />
            <Route path="/om-oss" element={<About />} />
            <Route 
              path="/scan" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyTicketScanning />
                </Suspense>
              } 
            />
            
            {/* Redirect for backward compatibility */}
            <Route path="/shows" element={<Navigate to="/forestallningar" replace />} />
            
            {/* Lazy loaded routes with intelligent prefetching */}
            <Route 
              path="/forestallningar" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyShows />
                </Suspense>
              }
            />
            <Route 
              path="/kurser" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyCourses />
                </Suspense>
              }
            />
            <Route 
              path="/kursledare" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyCourseLeaderOverview />
                </Suspense>
              }
            />
            <Route 
              path="/kursledare/:slug" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyCourseLeaderDetail />
                </Suspense>
              }
            />
            <Route 
              path="/lokal" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyLokal />
                </Suspense>
              }
            />
            <Route 
              path="/studentmanual" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyStudentmanual />
                </Suspense>
              }
            />
            <Route 
              path="/house-team" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyHouseTeam />
                </Suspense>
              }
            />
            <Route path="/houseteam" element={<Navigate to="/house-team" replace />} />
            <Route 
              path="/boka-oss" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyBokaOss />
                </Suspense>
              }
            />
            <Route 
              path="/forestallningar/:slug" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyShowDetails />
                </Suspense>
              }
            />
            <Route 
              path="/biljett/:ticketCode" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyTicketView />
                </Suspense>
              }
            />
            
            {/* Admin routes - heavily optimized */}
            <Route 
              path="/admin/*" 
              element={
                <SidebarProvider>
                  <div className="h-screen flex flex-col w-full">
                    <Suspense fallback={<GlobalLoader />}>
                      <LazyAdmin />
                    </Suspense>
                  </div>
                </SidebarProvider>
              }
            />
            
            {/* Ticket confirmation route */}
            <Route 
              path="/forestallningar/tack" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyTicketSuccess />
                </Suspense>
              }
            />
            
            {/* Course confirmation route */}
            <Route 
              path="/kurser/tack" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyCourseSuccess />
                </Suspense>
              }
            />
            
            {/* Course offer payment route */}
            <Route 
              path="/course-offer-payment/:token" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyCourseOfferPayment />
                </Suspense>
              }
            />
            
            {/* Payment routes - grouped */}
            <Route 
              path="/payment/*" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyPaymentPages />
                </Suspense>
              }
            />
            
            {/* Newsletter confirmation route */}
            <Route 
              path="/nyhetsbrev-bekraftelse" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyNewsletterConfirmation />
                </Suspense>
              }
            />
            
            {/* Newsletter unsubscribe routes */}
            <Route 
              path="/unsubscribe" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyUnsubscribe />
                </Suspense>
              }
            />
            <Route 
              path="/avprenumerera" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyUnsubscribe />
                </Suspense>
              }
            />
            
            {/* Integritetspolicy */}
            <Route 
              path="/integritet" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyIntegritet />
                </Suspense>
              }
            />

            {/* Om Improv */}
            <Route 
              path="/om-impro" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyOmImprov />
                </Suspense>
              }
            />

            {/* Vanliga frågor */}
            <Route 
              path="/vanliga-fragor" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyVanligaFragor />
                </Suspense>
              }
            />

            {/* Vår filosofi */}
            <Route 
              path="/var-filosofi" 
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyVarFilosofi />
                </Suspense>
              }
            />
            
            <Route
              path="/presentkort"
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyPresentkort />
                </Suspense>
              }
            />
            <Route
              path="/presentkort/tack"
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazyPresentkortTack />
                </Suspense>
              }
            />

            <Route
              path="/satt-losenord"
              element={
                <Suspense fallback={<GlobalLoader />}>
                  <LazySattLosenord />
                </Suspense>
              }
            />

            {/* QR-landningssida för GAFFA-annonsen (ej indexerad) */}
            <Route
              path="/gaffa"
              element={
                <Suspense fallback={null}>
                  <LazyGaffaRedirect />
                </Suspense>
              }
            />


            {/* Fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
      </main>
      {!isAdminRoute && <Footer />}
      <Suspense fallback={null}>
        <LazyToaster />
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
